import { redirectTo } from './history-service';
import { IAppSyncAPIConfig } from '../../api/graphql-api-client/graphql-api-client';
import { COMMON_PAGE_PATHS, graphQLDomainName, KvApiDomaninName, loginPagePath } from '../../constans/url-constants';
import { extractPathIdentifier, getPresenterLinkPath } from '../helpers/api-helpers';
import { authService } from './auth-service';

import {
  APP_SYNC_SUBSCRIPTIONS_EXCEPTIONS,
  COMMON_ERROR_CODES,
  DEFAULT_AUTH_FORBIDDEN_ERROR,
  DEFAULT_AUTH_UNAUTHORIZED,
  DEFAULT_ERROR_CODE,
  DEFAULT_ERROR_MESSAGE,
  FAILED_FETCH,
  generateNetworkErrorMessage,
  INFORMATION_ERROR_CODE,
  INVALID_PARAMETERS_ERROR,
  NETWORK_ERROR,
  RESOURCE_NOT_FOUND_CODE,
  SUBSCRIPTION_ERROR,
  TErrorCode
} from '../../constans/error-common-constants';
import { getMixpanelService } from './mixpanel-service-instance';
import { MIXPANEL_EVENT_IDENTIFIERS, MIXPANEL_EVENT_TYPES } from '../../constans/mixpanel-contants';

interface IResponseBase {
    response: Response;
    rawResponse?: string | Blob;
    networkFailureMessage?: string;
}

export interface IErrorObject {
    error_code: TErrorCode;
    error_message: string;
    error_details?: string;
}

interface IGraphQLErrorObject {
    message: string;
    errorType?: string;
    originalError?: IResponseBase;
}

export interface IGraphQLErrorResponse {
    errors: [IGraphQLErrorObject];
}

export interface IGraphQLSubscriptionError {
    provider?: IAwsAppSyncRealTimeProvider;
    error: IGraphQLErrorResponse;
}

interface IAwsAppSyncRealTimeProvider {
    isSSLEnabled: boolean;
    options: IAppSyncAPIConfig;
}


class ErrorParsingService {
  private readonly presenterEmbedId: string;
  private failedURLPath: string;

  constructor () {
    this.presenterEmbedId = extractPathIdentifier(COMMON_PAGE_PATHS.PRESENTATION) || '';
    this.failedURLPath = '';
  }

  public parseApiErrorMessage (errorObject: IErrorObject): string {
    return errorObject?.error_message || DEFAULT_ERROR_MESSAGE;
  }

  public parseApiErrorCode (errorObject: IErrorObject): string | number {
    return errorObject?.error_code || DEFAULT_ERROR_CODE;
  }

  public extractErrorMessage (response: IErrorObject): string {
    this.failedURLPath = KvApiDomaninName();
    const { error_code, error_message } = response;
    const networkErrorCode = response.toString().includes(FAILED_FETCH) ? FAILED_FETCH : DEFAULT_ERROR_CODE;

    const errorObject: IErrorObject = {
      error_code: error_code || networkErrorCode,
      error_message: error_message || DEFAULT_ERROR_MESSAGE
    };

    return this.handleErrorCodeExceptions(errorObject);
  }

  public extractSubscriptionError (response: IGraphQLErrorObject): string {
    const { message } = response;
    let errorCode = SUBSCRIPTION_ERROR;
    let errorMessage = message;

    if(message.includes(APP_SYNC_SUBSCRIPTIONS_EXCEPTIONS)) {
      errorCode = COMMON_ERROR_CODES.UNAUTHORIZED;
    } else if(message.includes(NETWORK_ERROR)) {
      this.failedURLPath = graphQLDomainName();
      errorCode = NETWORK_ERROR;
    }

    if(!message) {
      errorMessage = DEFAULT_ERROR_MESSAGE;
    }

    const errorObject: IErrorObject = {
      error_code: errorCode,
      error_message: errorMessage
    };
    return this.handleErrorCodeExceptions(errorObject);
  }

  public extractGraphQLError (response: IGraphQLErrorObject): string {
    const { message } = response;
    this.failedURLPath = graphQLDomainName();
    const unauthorizedCheck: TErrorCode = message.includes(APP_SYNC_SUBSCRIPTIONS_EXCEPTIONS) ? COMMON_ERROR_CODES.UNAUTHORIZED : DEFAULT_ERROR_CODE;
    const networkErrorCode = message.includes(NETWORK_ERROR) && NETWORK_ERROR;

    const errorObject: IErrorObject = {
      error_code: response.originalError?.response?.status || response.errorType || networkErrorCode || unauthorizedCheck,
      error_message: message || DEFAULT_ERROR_MESSAGE
    };
    return this.handleErrorCodeExceptions(errorObject);
  }

  private handlePathRedirect = (path: string) => {
    const { appUri } = getPresenterLinkPath();
    redirectTo(`${appUri}/${path}`);
  };

  private handleErrorCodeExceptions (errorObject: IErrorObject): string {
    const { error_code, error_message } = errorObject;
    const errorCode: TErrorCode = error_code && error_code.toString();
    switch (errorCode) {
      case COMMON_ERROR_CODES.BAD_REQUEST:
      case COMMON_ERROR_CODES.FORBIDDEN:
      case COMMON_ERROR_CODES.INTERNAL_ERROR:
      case COMMON_ERROR_CODES.SERVICE_UNAVAILABLE:
      case INVALID_PARAMETERS_ERROR:
      case INFORMATION_ERROR_CODE:
        return error_message;
      case COMMON_ERROR_CODES.NOT_FOUND:
      case RESOURCE_NOT_FOUND_CODE:
        this.handlePathRedirect(COMMON_PAGE_PATHS.NOT_FOUND);
        return error_message;
      case COMMON_ERROR_CODES.UNAUTHORIZED:
      case DEFAULT_AUTH_UNAUTHORIZED:
      case DEFAULT_AUTH_FORBIDDEN_ERROR:
        authService.clearJwt(this.presenterEmbedId);
        this.handlePathRedirect(loginPagePath(this.presenterEmbedId));
        return error_message;
      case SUBSCRIPTION_ERROR:
        getMixpanelService().track(MIXPANEL_EVENT_IDENTIFIERS.ERROR, MIXPANEL_EVENT_TYPES.ERROR_WEBSOCKET);
        return error_message;
      case NETWORK_ERROR:
      case FAILED_FETCH:
        getMixpanelService().track(MIXPANEL_EVENT_IDENTIFIERS.ERROR, MIXPANEL_EVENT_TYPES.ERROR_FIREWALL);
        return generateNetworkErrorMessage(this.failedURLPath);
      case DEFAULT_ERROR_CODE:
        getMixpanelService().track(MIXPANEL_EVENT_IDENTIFIERS.ERROR, MIXPANEL_EVENT_TYPES.ERROR_REFRESH_PAGE);
        return DEFAULT_ERROR_MESSAGE;
      default:
        getMixpanelService().track(MIXPANEL_EVENT_IDENTIFIERS.ERROR, MIXPANEL_EVENT_TYPES.ERROR_REFRESH_PAGE);
        return DEFAULT_ERROR_MESSAGE;
    }
  }
}

export default ErrorParsingService;
